import { useCallback, useMemo } from "react";
import { atom, useAtom } from "jotai";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import type { EventListGridItem } from "src/http/api-types";
import { useContentContext } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import type { GridItemsFilter } from "src/website-apps/shared/components/events-grid/PerformerEventGridSurface";
import type { DayPassOption } from "./ga-day-pass-selector";
import { DEFAULT_PASS_TYPE_FILTER, type PassTypeFilter } from "./ga-pass-type-chips";
import {
  deriveGridFilterData,
  EMPTY_GRID_ITEMS,
  isUnclassifiedEvent,
  matchesCityFilter,
  matchesDayCountFilter,
  matchesPassTypeFilter,
  matchesWeekendFilter,
} from "./ga-utils";
import type { WeekendInfo } from "./ga-weekend-selector";

/**
 * Minimum number of events (for the active pass type at the active location)
 * required before weekend or day-pass toggles are shown. Below this threshold
 * every option would have at most one or two cards, so toggles add noise.
 */
const MIN_EVENTS_FOR_TOGGLES = 2;

// ─── Atoms (singleton state) ────────────────────────────────────────────
const passTypeFilterAtom = atom<PassTypeFilter | null>(DEFAULT_PASS_TYPE_FILTER);
const selectedWeekendAtom = atom<string | null>(null);
const selectedCityAtom = atom<string | null>(null);
const selectedPassLengthAtom = atom<number | null>(null);

export interface GaFilterState {
  // --- User selections ---
  passTypeFilter: PassTypeFilter | null;
  setPassTypeFilter: (filter: PassTypeFilter | null) => void;
  selectedWeekend: string | null;
  setSelectedWeekend: (key: string | null) => void;
  selectedCity: string | null;
  setSelectedCity: (city: string | null) => void;
  selectedPassLength: number | null;
  setSelectedPassLength: (length: number | null) => void;

  // --- Derived / effective values ---
  effectiveWeekend: string | null;
  effectivePassLength: number | null;
  effectiveCity: string | null;

  // --- Derived data for UI rendering ---
  availableFilters: ReadonlySet<PassTypeFilter>;
  weekends: readonly WeekendInfo[];
  dayPassOptions: readonly DayPassOption[];
  hasWeekends: boolean;
  hasDayPasses: boolean;

  filterItems: GridItemsFilter;
}

/**
 * Hook providing all GA filter state and derived data. Reads from jotai
 * atoms (singleton state) and model data. Returns null when the surface
 * is not a GA festival.
 */
export const useGaFilter = (): GaFilterState | null => {
  const { performerGridSurface: surface, citySuggestions, seoCity, paidSearchQueryCity } = usePerformerPageModelData();
  const isGaMode = surface?.type === "ga-festival";

  const [passTypeFilter, setPassTypeFilterRaw] = useAtom(passTypeFilterAtom);
  const [selectedWeekend, setSelectedWeekend] = useAtom(selectedWeekendAtom);
  const [selectedCity, setSelectedCityRaw] = useAtom(selectedCityAtom);
  const [selectedPassLength, setSelectedPassLength] = useAtom(selectedPassLengthAtom);

  // Reset sub-filters when pass type changes
  const setPassTypeFilter = useCallback(
    (filter: PassTypeFilter | null) => {
      setPassTypeFilterRaw(filter);
      setSelectedWeekend(null);
      setSelectedPassLength(null);
    },
    [setPassTypeFilterRaw, setSelectedWeekend, setSelectedPassLength],
  );

  // Reset all filters when city changes
  const setSelectedCity = useCallback(
    (city: string | null) => {
      setSelectedCityRaw(city);
      setPassTypeFilterRaw(DEFAULT_PASS_TYPE_FILTER);
      setSelectedWeekend(null);
      setSelectedPassLength(null);
    },
    [setSelectedCityRaw, setPassTypeFilterRaw, setSelectedWeekend, setSelectedPassLength],
  );

  const classifications = isGaMode ? surface.gaPassClassifications : undefined;
  const gridItems = isGaMode ? surface.eventsGrid.items : EMPTY_GRID_ITEMS;

  // Labels are built per item, so resolve through the content context rather than a per-id hook.
  const { formattedContentResolver } = useContentContext();

  // ─── Effective city ───────────────────────────────────────────────────
  const hasMultipleLocations = (citySuggestions?.length ?? 0) > 1;
  const defaultCity = seoCity?.cityName ?? paidSearchQueryCity?.cityName ?? citySuggestions?.[0]?.city ?? null;
  const effectiveCity = hasMultipleLocations ? (selectedCity ?? defaultCity) : null;

  // ─── Single-pass derivation over gridItems ────────────────────────────
  const derived = useMemo(
    () => deriveGridFilterData(gridItems, classifications, effectiveCity, passTypeFilter),
    [classifications, gridItems, effectiveCity, passTypeFilter],
  );

  // ─── Available filters (pass-type chips) ──────────────────────────────
  const availableFilters: ReadonlySet<PassTypeFilter> = derived.filters;

  // ─── Weekend selector data ────────────────────────────────────────────
  const sortedWeekendKeys = useMemo(() => [...derived.weekendKeys].toSorted(), [derived.weekendKeys]);

  const hasMultipleWeekends = sortedWeekendKeys.length > 1 && derived.eventCount > MIN_EVENTS_FOR_TOGGLES;
  const effectiveWeekend = hasMultipleWeekends ? (selectedWeekend ?? sortedWeekendKeys[0] ?? null) : null;

  const weekends = useMemo<readonly WeekendInfo[]>(() => {
    return sortedWeekendKeys.map((key) => {
      const num = key.replaceAll(/\D/g, "");
      // "Weekend {0}"
      const label = formattedContentResolver("cid$#4290708#", num);
      const info: WeekendInfo = { key, label };
      const dateRange = derived.weekendDateRanges.get(key);
      if (dateRange) info.dateRange = dateRange;
      return info;
    });
  }, [sortedWeekendKeys, formattedContentResolver, derived.weekendDateRanges]);

  // ─── Day-pass selector data ───────────────────────────────────────────
  const sortedDayCounts = useMemo(() => [...derived.dayCounts].toSorted((a, b) => a - b), [derived.dayCounts]);

  const hasMultipleDayCounts =
    !hasMultipleWeekends && sortedDayCounts.length > 1 && derived.eventCount > MIN_EVENTS_FOR_TOGGLES;

  const isLongestDurationVariant =
    useSimpleFeatureTernary(
      FeatureSetEnum.TMPerformerGaDefaultLongestDuration,
      false,
      true,
      () => hasMultipleDayCounts,
    ) ?? false;

  const defaultPassLength = isLongestDurationVariant ? sortedDayCounts.at(-1) : sortedDayCounts[0];
  const effectivePassLength = hasMultipleDayCounts ? (selectedPassLength ?? defaultPassLength ?? null) : null;

  const dayPassOptions = useMemo<readonly DayPassOption[]>(() => {
    const options = sortedDayCounts.map((dc) => ({
      passLength: dc,
      // "{0} Day"
      label: formattedContentResolver("cid$#4297127#", dc),
    }));
    return isLongestDurationVariant ? options.toReversed() : options;
  }, [sortedDayCounts, formattedContentResolver, isLongestDurationVariant]);

  const hasWeekends = hasMultipleWeekends;
  const hasDayPasses = hasMultipleDayCounts;

  // ─── Filter function ──────────────────────────────────────────────────
  const filterItems = useCallback(
    (items: readonly EventListGridItem[]): readonly EventListGridItem[] => {
      if (!classifications) return items;
      const classified: EventListGridItem[] = [];
      const unclassified: EventListGridItem[] = [];

      for (const item of items) {
        const c = classifications[String(item.eventId)];
        if (!matchesCityFilter(effectiveCity, item.venueCity)) continue;

        if (isUnclassifiedEvent(c)) {
          if (matchesPassTypeFilter(passTypeFilter, c?.category)) {
            // Honor available metadata — only skip filters whose values are genuinely absent.
            const grouping = c?.grouping ?? null;
            if (grouping !== null && !matchesWeekendFilter(effectiveWeekend, grouping)) {
              continue;
            }
            const dayCount = c?.dayCount ?? null;
            if (dayCount !== null && !matchesDayCountFilter(effectivePassLength, dayCount)) {
              continue;
            }
            unclassified.push(item);
          }
          continue;
        }
        // Logically unreachable — isUnclassifiedEvent(c) above already catches
        // undefined and continues. But isUnclassifiedEvent returns boolean, not a
        // type predicate, so TS can't narrow c from GaPassClassification | undefined
        // to GaPassClassification in the code below. Without this guard every
        // c.category / c.grouping / c.dayCount reference would fail with TS18048.
        if (!c) continue;
        if (
          matchesPassTypeFilter(passTypeFilter, c.category) &&
          matchesWeekendFilter(effectiveWeekend, c.grouping) &&
          matchesDayCountFilter(effectivePassLength, c.dayCount ?? null)
        ) {
          classified.push(item);
        }
      }

      // Properly classified events first, unclassified/None at the end.
      return [...classified, ...unclassified];
    },
    [classifications, passTypeFilter, effectiveWeekend, effectiveCity, effectivePassLength],
  );

  if (!isGaMode) return null;

  return {
    passTypeFilter,
    setPassTypeFilter,
    selectedWeekend,
    setSelectedWeekend,
    effectiveWeekend,
    selectedCity,
    setSelectedCity,
    effectiveCity,
    selectedPassLength,
    setSelectedPassLength,
    effectivePassLength,
    availableFilters,
    weekends,
    dayPassOptions,
    hasWeekends,
    hasDayPasses,
    filterItems,
  };
};
